<template>
  <wizard-form v-on="$listeners" v-bind="$attrs">
    <template #content>
      <v-row>
        <v-col cols="12">
          <h1 class="primary--text font-weight-medium">
            {{ $t('transfer_local_panama') }}
          </h1>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_alias')"
            rules="required|max:200"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="`${$t('transfer_alias')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.alias"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="200"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_account_type')"
            rules="required"
            v-slot="{ errors }"
          >
            <v-select
              v-model="permanentInstruction.accountType"
              :items="items"
              :label="`${$t('transfer_account_type')} *`"
              :error-messages="errors"
              dense
              return-object
              item-text="t"
              @input="handleAccountType"
            >
              <template #item="{ item }">
                <span>{{ $t(item.t) }}</span>
              </template>

              <template #selection="{ item }">
                <span>{{ $t(item.t) }}</span>
              </template>
            </v-select>
          </ValidationProvider>
        </v-col>
        <v-col
          v-if="showBeneficiariesSelect"
          cols="12"
          md="6"
          class="d-flex flex-grow-1"
        >
          <ValidationProvider
            :name="$t('transfer_name')"
            rules="required|max:150"
            v-slot="{ errors }"
            class="flex-grow-1"
          >
            <v-select
              :label="`${$t('transfer_name')} *`"
              v-model="permanentInstruction.beneficiary"
              :items="beneficiaries"
              dense
              hide-details="auto"
              autocomplete="nope"
              :error-messages="errors[0]"
            />
          </ValidationProvider>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn
                v-if="lockedField"
                icon
                v-on="on"
                @click="handleUnlock"
                :disabled="!lockedField"
              >
                <v-icon>mdi-lock-open-variant</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('modify_beneficiary') }}</span>
          </v-tooltip>
        </v-col>
        <v-col v-else :cols="12" :md="6" class="d-flex flex-grow-1">
          <ValidationProvider
            class="flex-grow-1"
            :name="$t('transfer_name')"
            rules="required|max:150"
            v-slot="{ errors }"
          >
            <v-text-field
              class="flex-grow-1"
              ref="beneficiary"
              :label="`${$t('transfer_name')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.beneficiary"
              :error-messages="errors[0]"
              :disabled="lockedField"
              hide-details="auto"
              maxlength="150"
              @input="handleInputs"
            />
          </ValidationProvider>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn
                v-if="lockedField"
                icon
                v-on="on"
                @click="handleUnlock"
                :disabled="!lockedField"
              >
                <v-icon>mdi-lock-open-variant</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('modify_beneficiary') }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row v-if="isTypeNoBank" :key="updateKey">
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_account_number_iban')"
            rules="required|alpha_num|max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="`${$t('transfer_account_number_iban')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfBankAccNumber"
              :error-messages="errors[0]"
              @input="handleInputs"
              hide-details="auto"
              maxlength="50"
              v-mask="'XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <ValidationProvider
            ref="email"
            :name="$t('transfer_email')"
            rules="email|confirmed:correo|max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="$t('transfer_email')"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfEmail"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="50"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_confirm_email')"
            rules="email|max:50"
            vid="correo"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="$t('transfer_confirm_email')"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfEmailConfirm"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="50"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_financial_institution_of_beneficiary')"
            rules="required|max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="`${$t('transfer_financial_institution_of_beneficiary')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfBankName"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="50"
            />
          </ValidationProvider>
        </v-col>

        <v-col v-if="!isTypeNoBank" cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_account_number_iban')"
            rules="required|alpha_num|max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="`${$t('transfer_account_number_iban')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfBankAccNumber"
              :error-messages="errors[0]"
              @input="handleInputs"
              hide-details="auto"
              maxlength="50"
              v-mask="'XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
      <v-row v-if="permanentInstruction.accountType.value == 'nfiAccount'">
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_receiving_bank')"
            rules="required|max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="`${$t('transfer_receiving_bank')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.nfiName"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="50"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_account_receiving_bank')"
            rules="required|alpha_num|max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="`${$t('transfer_account_receiving_bank')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.nfiAccountNumber"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="50"
              v-mask="'XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            label="ABA / SWIFT"
            dense
            autocomplete="nope"
            v-model="permanentInstruction.bnfBankABA"
            hide-details="auto"
            maxlength="50"
          />
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_city')"
            rules="max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="$t('transfer_city')"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfBankCity"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="50"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_country')"
            rules=""
            v-slot="{ errors }"
          >
            <v-select
              v-model="permanentInstruction.bnfBankCountry"
              :items="countries"
              item-text="countryName"
              item-value="countryName"
              :label="$t('transfer_country')"
              :error-messages="errors[0]"
              @input="selectCountry($event, 'bnfBankCountryCode')"
              dense
              hide-details="auto"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-text-field
            :label="$t('transfer_observations')"
            dense
            autocomplete="nope"
            v-model="permanentInstruction.bnfReference"
            :disabled="permanentInstruction.accountType.value == 'nfiAccount'"
            hide-details="auto"
            maxlength="128"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            :label="$t('transfer_instructions')"
            dense
            autocomplete="nope"
            v-model="permanentInstruction.avsInternalRemark"
            maxlength="128"
            hide-details="auto"
          />
        </v-col>
      </v-row>
    </template>
  </wizard-form>
</template>
<script>
import WizardForm from '@/components/commons/WizardForm'
import { mapState } from 'vuex'

export default {
  components: {
    WizardForm
  },
  props: {
    permanentInstruction: {
      type: Object,
      required: true
    },
    partnerBanks: {
      type: Array,
      required: true
    },
    countries: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      updateKey: 1,
      lockedField: true,
      showBeneficiariesSelect: true,
      anotherBank: true,
      partnerBank: 'Otra',
      items: [
        { value: 'localCheckingAccount', t: 'transfer_account_type_1' },
        { value: 'localSavingsAccount', t: 'transfer_account_type_2' },
        { value: 'nfiAccount', t: 'transfer_account_type_3' }
      ],
      banks: ['Panacor', 'Banesco'],
      isThirdParty: false
    }
  },
  computed: {
    beneficiaries() {
      let items = []
      const main = this.accountData.firstname + ' ' + this.accountData.lastname
      items.push(main)

      if (this.accountData.type.toUpperCase() === 'JURIDICA') {
        const beneficiaries = this.accountData.beneficiaries
          .map(item => item.firstname + ' ' + item.lastname)
          .filter(item => item.trim() !== '')

        items = items.concat(beneficiaries)
      } else if (
        this.accountData.coOwners &&
        this.accountData.coOwners.trim() !== ''
      ) {
        items.push(this.accountData.coOwners)
      }

      return items
    },
    nationalBanks() {
      let banks = this.partnerBanks.filter(bank => bank.prtBankType == 'local')
      banks.push({ prtBankName: this.$t('transfer_other') })
      return banks
    },
    isExecutive() {
      return (
        this.user?.authorityLevelsCode === 1 ||
        this.user?.authorityLevelsCode === 2 ||
        this.user?.authorityLevelsCode === 4
      )
    },
    isAccountCode() {
      return (
        this.user?.avobjects?.findIndex(
          item =>
            item.code === this.account.code && item.typeCode === 'accountCode'
        ) > -1
      )
    },
    isTypeNoBank() {
      return this.permanentInstruction?.accountType.t === 'transfer_account_type_3'
    },
    ...mapState('account', ['account', 'accountData']),
    ...mapState('auth', ['user'])
  },
  watch: {
    isTypeNoBank() {
      this.updateKey += 1
    }
  },
  methods: {
    setBeneficiary() {
      this.permanentInstruction.isThirdParty = this.isThirdParty
    },
    handleAccountType() {
      this.partnerBank = ''
      if (this.permanentInstruction.accountType.value == 'nfiAccount') {
        this.$emit('changeAccType')
      } else {
        this.clear()
      }
      this.setBeneficiary()
    },
    handleInputs() {
      if (this.permanentInstruction.accountType.value == 'nfiAccount')
        this.$emit('changeAccType')
    },
    selectCountry(country, model) {
      const selectedCountry = this.countries.find(
        item => item.countryName == country
      )
      this.$emit('selectCountry', { model, code: selectedCountry.countryCode })
    },
    handlePartnerBank(bank) {
      if (bank == this.$t('transfer_other')) {
        this.anotherBank = true
        this.$emit('clearBank')
      } else {
        this.anotherBank = false
        this.$emit('selectPartnerBank', bank)
      }
    },
    async handleUnlock() {
      const text = !this.isExecutive
        ? this.$t('warning_instructions_payments_to_third_parties_customer')
        : this.$t('warning_instructions_payments_to_third_parties_1') +
          ' ' +
          this.$t('warning_instructions_payments_to_third_parties_2')

      const params = {
        text,
        title: this.$t('twofactor_modal_warning_title'),
        showClose: false,
        persistent: true,
        actions: {
          true: {
            text: this.$t('btn_accept'),
            color: 'primary'
          }
        }
      }

      await this.$dialog.info(params)

      if (this.isExecutive) {
        this.isThirdParty = true
        this.lockedField = false
        this.showBeneficiariesSelect = false
        this.permanentInstruction.beneficiary = ''
        this.permanentInstruction.bnfEmail = ''
        this.permanentInstruction.bnfEmailConfirm = ''
        this.permanentInstruction.isThirdParty = true
        // Set focus
        this.$nextTick(() => this.$refs.beneficiary.focus())
      }
    },
    async clear() {
      this.permanentInstruction.bnfAddres1 = ''
      this.permanentInstruction.bnfAddres2 = ''
      this.permanentInstruction.bnfZipcode = ''
      this.permanentInstruction.bnfCity = ''
      this.permanentInstruction.bnfState = ''
      this.permanentInstruction.bnfCountry = ''
      this.permanentInstruction.bnfCountryCode = ''
      this.permanentInstruction.bnfPhone = ''
      this.permanentInstruction.bnfFurtherCredit = ''
      this.permanentInstruction.bnfBankABA = ''
      this.permanentInstruction.bnfBankAddres1 = ''
      this.permanentInstruction.bnfBankAddres2 = ''
      this.permanentInstruction.bnfBankCity = ''
      this.permanentInstruction.bnfBankState = ''
      this.permanentInstruction.bnfBankCountry = ''
      this.permanentInstruction.bnfBankCountryCode = ''
      this.permanentInstruction.bnfReceivingBank = ''
      this.permanentInstruction.bnfReceivingAccNumber = ''
      this.permanentInstruction.nfiName = ''
      this.permanentInstruction.nfiAccountNumber = ''
      this.permanentInstruction.intermBankIsUsed = false
      this.permanentInstruction.intermBankName = ''
      this.permanentInstruction.intermBankAccNumber = ''
      this.permanentInstruction.intermBankABA = ''
      this.permanentInstruction.intermBankAddres1 = ''
      this.permanentInstruction.intermBankAddres2 = ''
      this.permanentInstruction.intermBankCity = ''
      this.permanentInstruction.intermBankState = ''
      this.permanentInstruction.intermBankCountry = ''
      this.permanentInstruction.intermBankCountryCode = ''
      this.permanentInstruction.intermReference = ''
      this.permanentInstruction.bnfReference = ''
    }
  }
}
</script>
<style scoped>
.v-stepper__step__step__paragraf__ligth {
  text-align: left;
  font: normal normal normal 14px/16px Lato;
  letter-spacing: 0px;
  color: #686f8d;
  opacity: 1;
}
.v-stepper__step__step_subtitles__blacks {
  text-align: left;
  font: normal normal bold 16px/38px Lato;
  letter-spacing: 0px;
  color: #212529;
  opacity: 1;
}
@media (max-width: 979px) {
  .v-stepper__step__step_subtitles__blacks {
    font: normal normal bold 14px/38px Lato;
  }
}

.main-container {
  line-height: normal;
}

hr {
  margin: 0;
  padding: 0;
}

hr.rounded {
  border-top: 2px solid;
  width: 400px;
}
</style>
